import React, { useState } from "react"

import Button from "~/components/Button"

import { GridCell } from "./GridCell"
import type { MonthData, DisplayUnit } from "./types"

interface GridViewProps {
  monthlyData: MonthData[]
  displayUnit: DisplayUnit
  onCellChange: (monthIndex: number, value: number | null) => void
  onStartOver: () => void
  onRefineEstimates: () => void
  onToggleUnit: (unit: DisplayUnit) => void
  hasUserEdits: boolean
  estimateSource: string | null
  isRefining?: boolean
  canEdit: boolean
}

export const GridView: React.FC<GridViewProps> = ({
  monthlyData,
  displayUnit,
  onCellChange,
  onStartOver,
  onRefineEstimates,
  onToggleUnit,
  hasUserEdits,
  estimateSource,
  isRefining = false,
  canEdit,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  const handleTabNext = (currentIndex: number) => {
    if (currentIndex < 11) {
      setEditingIndex(currentIndex + 1)
    } else {
      setEditingIndex(null)
    }
  }

  const handleTabPrev = (currentIndex: number) => {
    if (currentIndex > 0) {
      setEditingIndex(currentIndex - 1)
    } else {
      setEditingIndex(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-black">
          {estimateSource
            ? `Smart Estimates from ${estimateSource}`
            : `Energy Consumption`}
        </h3>
        <Button
          type="button"
          onClick={onStartOver}
          variant="solid"
          disabled={!canEdit}
        >
          Start Over
        </Button>
      </div>

      {/* Legend and Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded border-2 border-gray-200 bg-gray-100"></div>
            <span className="text-sm text-gray-600">Estimated</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded border-2 border-green-200 bg-green-100"></div>
            <span className="text-sm text-gray-600">Manually Entered</span>
          </div>
        </div>

        {/* Unit Toggle */}
        <div className="flex min-w-[120px] items-center rounded-full bg-gray-100 p-1">
          <button
            type="button"
            className={`flex-1 rounded-full px-6 py-1.5 text-sm font-medium transition-colors ${
              displayUnit === `$`
                ? `bg-white text-black shadow-sm`
                : `text-gray-600 hover:text-gray-800`
            }`}
            onClick={() => canEdit && onToggleUnit(`$`)}
            disabled={!canEdit}
          >
            $
          </button>
          <button
            type="button"
            className={`flex-1 rounded-full px-6 py-1.5 text-sm font-medium transition-colors ${
              displayUnit === `kWh`
                ? `bg-white text-black shadow-sm`
                : `text-gray-600 hover:text-gray-800`
            }`}
            onClick={() => canEdit && onToggleUnit(`kWh`)}
            disabled={!canEdit}
          >
            kWh
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-6 gap-2">
        {monthlyData.map((month) => (
          <GridCell
            key={month.index}
            month={month.name}
            value={
              displayUnit === `$`
                ? (month.costValue ?? null)
                : (month.kwhValue ?? null)
            }
            source={month.source}
            displayUnit={displayUnit}
            onValueChange={(value) => onCellChange(month.index, value)}
            isEditable={canEdit}
            isEditing={editingIndex === month.index}
            onEditStart={() => setEditingIndex(month.index)}
            onEditEnd={() => setEditingIndex(null)}
            onTabNext={() => handleTabNext(month.index)}
            onTabPrev={() => handleTabPrev(month.index)}
          />
        ))}
      </div>

      {/* Instructional text or Refine Button */}
      {!hasUserEdits ? (
        <div className="flex justify-center">
          <p className="text-sm text-gray-600">
            Click any month to manually enter a value
          </p>
        </div>
      ) : (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onRefineEstimates}
            disabled={!canEdit || isRefining}
            className="rounded bg-green-500 px-8 py-2 font-medium text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-green-400"
          >
            {isRefining ? `Refining Estimates...` : `Refine Estimates`}
          </button>
        </div>
      )}
    </div>
  )
}
